import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { map } from 'rxjs/operators';
import { JoinResolverData } from 'src/app/pages/join/join.resolver';
import { AppState } from 'src/app/store/state';
import { AuthUser, OAuthCompany, SsoProviderType } from 'src/models';
import { ApiService } from '../api.service';
import { AuthService, CredentialAuthResponse, EditProfileProps, LoginResult, SsoOtpResponse, TokenResult, TokenScope } from './auth.service';

@Injectable({ providedIn: 'root' })
export class CookieAuthService extends AuthService {
  readonly version = 'v2';

  constructor(api: ApiService, store: Store) {
    super(api, store);
  }

  refreshToken(): Promise<unknown> {
    return this.api.request('post', 'auth/token/refresh', {}, { withCredentials: true }, true, '2.0').toPromise();
  }

  login(email: string, password: string, totpCode?: string) {
    return this.api
      .request<{ data: LoginResult }>('post', `auth/v2/login`, {
        email,
        password,
        totpCode,
      }, { withCredentials: true }, true, '2.0')
      .pipe(map(x => x?.data || x as any || {} as LoginResult))
      .toPromise();
  }

  logout() {
    return this.api.request('post', `auth/v2/logout`, {}, { withCredentials: true }, true, '2.0').toPromise();
  }

  auth() {
    return this.api
      .request<{ data: AuthUser }>('get', `auth/user/me`, { 'has-managed-screencasts': true }, { withCredentials: true }, true, '2.0')
      .pipe(map(x => x?.data))
      .toPromise();
  }

  resetPassword(token: string, password: string) {
    // Note: this is still using the old (v1) endpoint
    return this.api.request('put', `profile`, { password }, { withCredentials: true, params: { company: '' }, headers: this.getTokenHeader(token) }, true).toPromise();
  }

  forgotPassword(email: string) {
    // Note: this is still using the old (v1) endpoint
    return this.api.request('get', `profile/restore-password`, { email, auth: '2' }, { withCredentials: true }, true).toPromise();
  }

  getScopedToken(scope: TokenScope): Promise<string> {
    return this.api
      .request<{ token: string }>('get', `auth/token/${scope}`, {}, { withCredentials: true }, true, '2.0')
      .pipe(map(x => x?.token))
      .toPromise();
  }

  getTokenHeader(token: string): Record<string, string> {
    return { Authorization: `Bearer ${token}` };
  }

  oauthLogin(provider: SsoProviderType, token: string) {
    return this.api.request<{ data: { token: string } }>
      ('post', `auth/v2/login/sso/${provider}`, {}, { withCredentials: true, params: { company: '' }, headers: { Authorization: `JWT ${token}` } }, true, '2.0')
      .pipe(map(x => x?.data))
      .toPromise();
  }

  findCompaniesByOTP(email: string, issuer: string, otp: string) {
    return this.api.request<{ data: OAuthCompany[] }>
      ('post', `auth/v2/sso/find-my-company${otp ? `/${otp}` : ''}`, email ? { email } : { issuer }, { withCredentials: true, params: { company: '' } }, true, '2.0')
      .pipe(map(x => x?.data))
      .toPromise();
  }

  isSsoOtpEnabled(email: string) {
    return this.api.request<SsoOtpResponse>('post', `auth/sso/otp/verify`, { email }, { withCredentials: true }, true, '2.0').toPromise();
  }

  sendOtpRequest(email: string) {
    return this.api.request<{ data: {} }>('post', `auth/sso/otp/generate`, { email }, { withCredentials: true }, true, '2.0').toPromise();
  }

  register(email: string, name: string, password: string, params?: any) {
    return this.api.request<{ data: TokenResult }>('post', 'auth/v2/register', { name, email, password, ...(params || {}) }, { withCredentials: true }, true, '2.0')
      .pipe(map(x => x?.data || {} as TokenResult))
      .toPromise();
  }

  editProfile(properties: EditProfileProps) {
    return this.api.request<{ data: TokenResult }>('put', `profile`, properties, { withCredentials: true }, true).pipe(map(x => x.data)).toPromise();
  }

  validateMagicLinkToken(token: string): Promise<JoinResolverData> {
    return this.api.request<JoinResolverData>('post', 'register/validate-token', { token }, { withCredentials: true }, true)
      .toPromise().catch(() => ({ valid: false }));
  }

  oauthGoogleLogin(token: string): Promise<CredentialAuthResponse> {
    const payload = this.parseJwt(token);
    const userEmail = payload['email'];
    if (!userEmail) return null;
    const body = {
      email: userEmail,
      timezone: Intl && Intl.DateTimeFormat().resolvedOptions().timeZone,
      splitTest: [
        { name: 'popup-signup-trigger', value: 'login-form' },
        { name: 'google-auth', value: true },
      ],
    };

    return this.api.request('post', 'auth/v2/oauth2/google/auth', body, { withCredentials: true, headers: { Authorization: `JWT ${token}` } }, true, '2.0').toPromise();
  }
}

@Injectable({ providedIn: 'root' })
export class CookieAuthMigrateService extends CookieAuthService {
  migrateToken(oldToken: string) {
    return this.api.request('post', 'auth/token/migrate-v1', {}, {
      headers: {
        'x-platform-scope': 'web',
        Authorization: `JWT ${oldToken}`,
      },
      withCredentials: true,
    }, true, '2.0');
  }

  override async auth(): Promise<AuthUser> {
    const token = this.store.selectSnapshot((st: AppState) => st.auth.token);
    await this.migrateToken(token).toPromise().catch(() => {
      // If migration fails (e.g. due to invalid/expired token),
      // just proceed to the auth call which will likely fail and log the user out
    });
    return super.auth();
  }
}
